import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  /** Body content; can be string or JSX (e.g. a list of file names) */
  body: React.ReactNode;
  /** When true, user must type the confirmWord exactly to enable the Delete button.
   *  Use this for bulk / cross-editor / dangerous deletes. */
  requireTypedConfirmation?: boolean;
  /** The word the user must type. Defaults to 'DELETE'. */
  confirmWord?: string;
  /** Label of the destructive button */
  confirmLabel?: string;
  /** External "in progress" flag to disable the button while submitting */
  busy?: boolean;
}

/**
 * Single source of truth for delete confirmations.
 *  - Per-file delete: simple yes/no
 *  - Bulk delete (N>1) or cross-editor delete: requires typing DELETE
 *
 * The typed-confirmation tier exists because of the 2026-05-03 incident where
 * 72 files belonging to 10 different editors were soft-deleted in 7 seconds via
 * a bulk action with no friction. Forcing the user to type DELETE prevents
 * accidental triggers (a stray click no longer destroys 72 files).
 */
export function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  body,
  requireTypedConfirmation = false,
  confirmWord = 'DELETE',
  confirmLabel = 'Delete',
  busy = false,
}: Props) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen]);

  if (!isOpen) return null;

  const canConfirm = !busy && (!requireTypedConfirmation || typed.trim() === confirmWord);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60">
      <div className="bg-card border border-red-300 dark:border-red-700 rounded-lg w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="text-base font-semibold text-red-700 dark:text-red-400">{title}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors" disabled={busy}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <div className="text-foreground">{body}</div>
          <p className="text-xs text-muted-foreground">
            Files are soft-deleted (recoverable by an admin), but they are immediately hidden from
            the request and editors can no longer see them.
          </p>

          {requireTypedConfirmation && (
            <div className="pt-2 border-t border-border">
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                Type <span className="font-mono font-bold text-red-600">{confirmWord}</span> to confirm
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={confirmWord}
                autoFocus
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background outline-none focus:border-red-500"
                disabled={busy}
              />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm rounded border border-border hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm()}
            disabled={!canConfirm}
            className="px-4 py-2 text-sm font-medium rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
